import {
    Card,
    CardHeader,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Typography
} from '@mui/material';
import {
    DataGrid,
    gridPageCountSelector,
    gridPageSelector,
    koKR,
    useGridApiContext,
    useGridSelector
} from '@mui/x-data-grid';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { apiService } from '../../api/axiosClient';
import CustomAvatar from '../../components/mui/avatar';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import { getInitials } from '../../utils/get-initials';
import UserToolbar from './UserToolbar';

const currencyLocale = Intl.NumberFormat('ko-KR');

const renderClient = params => {
    const { row } = params
    const stateNum = Math.floor(Math.random() * 6)
    const states = ['success', 'error', 'warning', 'info', 'primary', 'secondary']
    const color = states[stateNum]
  
    return (
      <CustomAvatar skin='light' color={color} sx={{ mr: 3, fontSize: '.8rem', width: '1.875rem', height: '1.875rem' }}>
        {getInitials(row.name ? row.name : 'User')}
      </CustomAvatar>
    )
}

const FindUser = (props) => {
    const {field='username', q, reload, setReload, selected, setSelected} = props;
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(0)
    const [total, setTotal] = useState(0)
    const [sort, setSort] = useState('asc')
    const [pageSize, setPageSize] = useState(10)
    const [list, setList] = useState([])
    const [searchValue, setSearchValue] = useState(q)
    const [sortColumn, setSortColumn] = useState('username')
    const [selectionModel, setSelectionModel] = useState([]);

    const loadMembers = async (sort, query, field, column) => {
        try {
            const response = await apiService.get('/users', {
                params: {
                    page,
                    pageSize,
                    query,
                    field,
                    sort,
                    column
                }
            });
            const payload = response.data;
            
            setTotal(payload.size);
            setList(payload.list);
            setLoading(false);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        loadMembers(sort, searchValue, field, sortColumn)
    }, [page, pageSize, reload])

    const handleSortModel = (newModel) => {
        if (newModel.length) {
            setSort(newModel[0].sort)
            setSortColumn(newModel[0].field)
            loadMembers(newModel[0].sort, searchValue, field, newModel[0].field)
        } else {
            setSort('asc')
            setSortColumn('username')
        }
    }

    const handleSearch = value => {
        setSearchValue(value)
        loadMembers(sort, value, field, sortColumn)
    }

    const onPageSizeChange = e => {
        setPageSize(e.target.value)
    }

    function CustomPagination() {
        const apiRef = useGridApiContext();
        const _page = useGridSelector(apiRef, gridPageSelector);
        const pageCount = useGridSelector(apiRef, gridPageCountSelector);
        
        return (
            <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
                <InputLabel id="pagesize-select">행/페이지</InputLabel>
                <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
                    <Select
                        labelId="pagesize-select"
                        id="pagesize-select"
                        value={pageSize}
                        onChange={onPageSizeChange}
                        label="pageSize"
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                    </Select>
                </FormControl>
                <Pagination
                    color="primary"
                    count={pageCount}
                    page={_page + 1}
                    onChange={(event, value) => apiRef.current.setPage(value - 1)}
                />
            </Stack>
        );
    }

    const columns = [
        {
            field: '__check__',
            hideable: false,
            sortable: false,
            filterable: false,
            headerAlign: 'left'
        },
        {
            minWidth: 220,
            field: 'username',
            hideable: false,
            headerName: 'User ID',
            renderCell: params => (
            <Fragment>
                {renderClient(params)}
                <Typography variant='body2' sx={{ ml: 1, color: 'text.primary' }}>
                    {params.row.username}
                </Typography>
            </Fragment>
            )
        },
        {
            minWidth: 100,
            field: 'name',
            hideable: false,
            headerName: '이름',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {params.row.name}
            </Typography>
            )
        },
        {
            minWidth: 100,
            field: 'com_name',
            hideable: false,
            headerName: '업체명',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {params.row.com_name}
            </Typography>
            )
        },
        {
            minWidth: 100,
            field: 'com_sector',
            hideable: false,
            headerName: '업체분류',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {params.row.com_sector}
            </Typography>
            )
        },
        {
            minWidth: 100,
            field: 'com_charge',
            hideable: false,
            headerName: '담당자',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {params.row.com_charge}
            </Typography>
            )
        },
        {
            field: 'point',
            minWidth: 50,
            headerName: '누적포인트',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {currencyLocale.format(params.row.point)}
            </Typography>
            )
        },
        {
            field: 'regtime',
            minWidth: 50,
            headerName: '등록날짜',
            renderCell: params => (
              <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {moment(params.row.regtime).format("YYYY-MM-DD")}
              </Typography>
            )
        }
    ]

    return (
        loading ? <Spinner/> :
        <Card>
            <CardHeader title='고객 리스트' />
            <DataGrid
                sx={{
                    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': { display: 'none'},
                    ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
                }}
                localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                autoHeight
                getRowId={row => row.userid}
                pagination
                rows={list}
                rowCount={total || 0}
                columns={columns}
                pageSize={pageSize}
                sortingMode='server'
                paginationMode='server'
                onSortModelChange={handleSortModel}
                onPageChange={newPage => setPage(newPage)}
                components={{
                    Toolbar: UserToolbar,
                    Pagination: CustomPagination,
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
                componentsProps={{
                    toolbar: {
                        value: searchValue,
                        clearSearch: () => handleSearch(''),
                        onChange: event => handleSearch(event.target.value),
                        numSelected: selected.length,
                        selected,
                        reload,
                        setReload
                    }
                }}
                checkboxSelection={true}
                disableSelectionOnClick={true}
                selectionModel={selectionModel}
                onSelectionModelChange={(selection) => {
                    if (selection.length === 0) {
                        // 선택된 것이 없으면 빈 객체로 설정
                        setSelected({});
                        setSelectionModel([]);
                        return;
                    }

                    const selectionSet = new Set(selectionModel);
                    const result = selection.filter((s) => !selectionSet.has(s));
                    setSelectionModel(result);

                    if (result.length > 0) {
                        list.find((item) => {
                            if (item.userid === result[0]) {
                                setSelected(item);
                            }
                        });
                    } else {
                        setSelected({});
                    }
                }}
            />
        </Card>
  )
}

export default FindUser
